/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Two-column row primitive that backs every section in the redesigned
// Settings modal. Figma uses a left "Title" frame of fixed width 180px
// and a flexible right column. The parent's `itemSpacing` is 16, so
// the row itself uses no vertical padding — the gap is owned by the
// container. See:
//
//   * 3542:4424 (Profile)    — Title 180×62, right column 655px wide.
//                              Title text is 20/28 here (hero variant).
//   * 3542:4436 (Body)       — same split. Title text 16/22 (standard).
//   * 3542:4435 (Line 293)   — divider, stroke #2F333B, 0.5px.
//   * 3542:4631 (Display pref) — same split, 3 theme cards in the
//                                right column.
//
// Section title text is Lato Bold 16/22.4 #F5F6F8 (standard) or
// 20/28 #F5F6F8 (hero, used for the very first row per pane).
// Section description text is Lato Regular 12/16.8 #B5BAC3.
// Both have light-mode variants via `dark:` prefix.

import type { ReactNode } from "react";
import { Text, View } from "react-native";

export interface SettingsRowProps {
	readonly title: string;
	readonly description?: string;
	readonly children?: ReactNode;
	readonly trailing?: ReactNode;
	// `hero` renders the title at 20/28 instead of 16/22 — matches the
	// first row of every pane (Profile / Appearance / Notifications /
	// etc. — Figma 3542:4426). The other rows under that first one
	// stay at the standard size.
	readonly variant?: "standard" | "hero";
	// Stable anchor for the per-pane guide tour. Maps to `data-testid`
	// on web so a tour step can spotlight this section.
	readonly testID?: string;
}

export function SettingsRow(props: SettingsRowProps): JSX.Element {
	const { title, description, children, trailing, variant = "standard", testID } =
		props;
	const isHero = variant === "hero";

	return (
		<View
			{...(testID !== undefined ? { testID } : {})}
			// 16px gap between the title column and the controls column.
			// No vertical padding — parent uses itemSpacing 16 between rows.
			className="
				flex flex-row items-start gap-4
			"
		>
			{/*
				Title column — Figma 3542:4425. Fixed 180px width regardless
				of viewport so the controls column stays aligned across rows.
			*/}
			<View className="w-[180px] shrink-0">
				<Text
					// Section title — Lato Bold:
					//   hero    20/28 (Figma 3542:4426 — first row per pane)
					//   standard 16/22 (Figma 3542:4438 — every subsequent row)
					// Both render in #F5F6F8 dark / #1C1C1E light.
					className={
						isHero
							? "text-[20px] leading-[28px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
							: "text-[16px] leading-[22px] font-bold text-light-text-primary dark:text-[#F5F6F8]"
					}
				>
					{title}
				</Text>
				{description != null && description.length > 0 && (
					<Text
						// Section description — Lato Regular 12/16.8 #B5BAC3.
						// `mt-1.5` (6px) matches Figma's implicit 2-6px gap
						// between hero title (h=28) + description in the
						// 180×62 title frame (62-28-16 = 18 → 6/12 split).
						className="
							mt-1.5 text-[12px] leading-[16px] font-normal
							text-light-text-secondary dark:text-[#B5BAC3]
						"
					>
						{description}
					</Text>
				)}
			</View>
			{/*
				Content column. Figma "Input fields" frame (3542:4439)
				uses itemSpacing 16 between stacked fields — switch from
				the legacy gap-3 to gap-4 to match.
			*/}
			<View className="flex-1 min-w-0 flex flex-col gap-4">{children}</View>
			{trailing != null && (
				<View className="shrink-0 flex flex-row items-center gap-2 pt-1">
					{trailing}
				</View>
			)}
		</View>
	);
}

// Horizontal hairline divider — Figma node 3542:4435 / 3542:4464 etc.
// stroke #2F333B at 0.5px. We render at 1px because sub-pixel borders
// don't paint reliably on react-native-web and RN-macos at @1x.
export function SettingsDivider(): JSX.Element {
	return (
		<View
			accessibilityElementsHidden
			className="
				h-px w-full
				bg-light-border-secondary dark:bg-[#2F333B]
			"
		/>
	);
}
